import { useRef, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';

import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants';
import { Input, Select } from '@/components/forms';
import { Button } from '@/components/ui';
import type { InvitationWizardFormData } from '@/types/wizard';
import styles from './EventDetailsStep.module.css';

const EVENT_TYPE_OPTIONS = [
  { value: 'Corporate Event', label: 'Corporate Event' },
  { value: 'Christmas Party', label: 'Christmas Party' },
  { value: 'New Year Celebration', label: 'New Year Celebration' },
  { value: 'Halloween Party', label: 'Halloween Party' },
  { value: 'Birthday', label: 'Birthday' },
  { value: 'Quinceañera', label: 'Quinceañera' },
  { value: 'Wedding', label: 'Wedding' },
  { value: 'Engagement Party', label: 'Engagement Party' },
  { value: 'Baby Shower', label: 'Baby Shower' },
  { value: 'Bridal Shower', label: 'Bridal Shower' },
  { value: 'Anniversary', label: 'Anniversary' },
  { value: 'Gala', label: 'Gala' },
  { value: 'Awards Ceremony', label: 'Awards Ceremony' },
  { value: 'Fundraiser', label: 'Fundraiser' },
  { value: 'Charity Event', label: 'Charity Event' },
  { value: 'Product Launch', label: 'Product Launch' },
  { value: 'Grand Opening', label: 'Grand Opening' },
  { value: 'Press Conference', label: 'Press Conference' },
  { value: 'Trade Show', label: 'Trade Show' },
  { value: 'Conference', label: 'Conference' },
  { value: 'Seminar', label: 'Seminar' },
  { value: 'Workshop', label: 'Workshop' },
  { value: 'Webinar', label: 'Webinar' },
  { value: 'Team Building', label: 'Team Building' },
  { value: 'Networking Event', label: 'Networking Event' },
  { value: 'Happy Hour', label: 'Happy Hour' },
  { value: 'Cocktail Party', label: 'Cocktail Party' },
  { value: 'Dinner Party', label: 'Dinner Party' },
  { value: 'Brunch', label: 'Brunch' },
  { value: 'Pool Party', label: 'Pool Party' },
  { value: 'Beach Party', label: 'Beach Party' },
  { value: 'Garden Party', label: 'Garden Party' },
  { value: 'Rooftop Party', label: 'Rooftop Party' },
  { value: 'Themed Party', label: 'Themed Party' },
  { value: 'Costume Party', label: 'Costume Party' },
  { value: 'Karaoke Night', label: 'Karaoke Night' },
  { value: 'Game Night', label: 'Game Night' },
  { value: 'Movie Night', label: 'Movie Night' },
  { value: 'Sports Event', label: 'Sports Event' },
  { value: 'Marathon / Run', label: 'Marathon / Run' },
  { value: 'Tournament', label: 'Tournament' },
  { value: 'Concert', label: 'Concert' },
  { value: 'Music Festival', label: 'Music Festival' },
  { value: 'Art Exhibition', label: 'Art Exhibition' },
  { value: 'Fashion Show', label: 'Fashion Show' },
  { value: 'Film Screening', label: 'Film Screening' },
  { value: 'Book Launch', label: 'Book Launch' },
  { value: 'Graduation', label: 'Graduation' },
  { value: 'Prom', label: 'Prom' },
  { value: 'Reunion', label: 'Reunion' },
  { value: 'Retirement Party', label: 'Retirement Party' },
  { value: 'Farewell Party', label: 'Farewell Party' },
  { value: 'Welcome Party', label: 'Welcome Party' },
  { value: 'Housewarming', label: 'Housewarming' },
  { value: 'Religious Ceremony', label: 'Religious Ceremony' },
  { value: 'Baptism', label: 'Baptism' },
  { value: 'First Communion', label: 'First Communion' },
  { value: 'Bar / Bat Mitzvah', label: 'Bar / Bat Mitzvah' },
  { value: 'Memorial / Tribute', label: 'Memorial / Tribute' },
  { value: 'Volunteer Event', label: 'Volunteer Event' },
  { value: 'Community Gathering', label: 'Community Gathering' },
  { value: 'Festival / Fair', label: 'Festival / Fair' },
  { value: 'Pop-Up Event', label: 'Pop-Up Event' },
  { value: 'Tasting Event', label: 'Tasting Event' },
  { value: 'Cooking Class', label: 'Cooking Class' },
  { value: 'Wellness / Yoga', label: 'Wellness / Yoga' },
  { value: 'Hackathon', label: 'Hackathon' },
  { value: 'Meetup', label: 'Meetup' },
  { value: 'Open House', label: 'Open House' },
  { value: 'VIP Experience', label: 'VIP Experience' },
  { value: 'Launch Party', label: 'Launch Party' },
  { value: 'End of Year Party', label: 'End of Year Party' },
  { value: 'Summer Party', label: 'Summer Party' },
  { value: 'Winter Wonderland', label: 'Winter Wonderland' },
  { value: 'Carnival', label: 'Carnival' },
  { value: 'Masquerade Ball', label: 'Masquerade Ball' },
  { value: 'Luau', label: 'Luau' },
  { value: 'Fiesta', label: 'Fiesta' },
];

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'];

// See EventDetails.tsx for rationale — same safety caps for the invitation wizard.
const NOTES_HARD_CAP = 50_000;
const NOTES_WARN_THRESHOLD = 2_000;

interface Props {
  formData: InvitationWizardFormData;
  updateForm: <K extends keyof InvitationWizardFormData>(
    key: K,
    value: InvitationWizardFormData[K],
  ) => void;
  onNext: () => void;
  onBack: () => void;
}

type OptionalField = 'time' | 'dress_code' | 'logo' | 'notes';

const OPTIONAL_FIELDS: { key: OptionalField; label: string }[] = [
  { key: 'time', label: 'Time' },
  { key: 'dress_code', label: 'Dress Code' },
  { key: 'logo', label: 'Logo' },
  { key: 'notes', label: 'Special Notes' },
];

export const EventDetailsStep = ({ formData, updateForm, onNext, onBack }: Props) => {
  const [eventTypeSearch, setEventTypeSearch] = useState('');
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const [visibleOptionals, setVisibleOptionals] = useState<Set<OptionalField>>(() => {
    const initial = new Set<OptionalField>();
    if (formData.time) initial.add('time');
    if (formData.dress_code) initial.add('dress_code');
    if (formData.logo_url) initial.add('logo');
    if (formData.special_notes) initial.add('notes');
    return initial;
  });

  const toggleOptional = (field: OptionalField) => {
    setVisibleOptionals((prev) => {
      const next = new Set(prev);
      if (next.has(field)) {
        next.delete(field);
        if (field === 'time') updateForm('time', '');
        if (field === 'dress_code') updateForm('dress_code', '');
        if (field === 'logo') updateForm('logo_url', '');
        if (field === 'notes') updateForm('special_notes', '');
      } else {
        next.add(field);
      }
      return next;
    });
  };

  const canProceed = Boolean(
    formData.event_name.trim() && formData.event_date.trim() && formData.venue.trim(),
  );

  const setNotesSafe = (v: string) => {
    updateForm('special_notes', v.length > NOTES_HARD_CAP ? v.slice(0, NOTES_HARD_CAP) : v);
  };
  const notesLength = formData.special_notes.length;
  const isLongNotes = notesLength > NOTES_WARN_THRESHOLD;

  const handleLogoUpload = useCallback(
    async (file: File) => {
      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) return;
      setIsUploadingLogo(true);
      try {
        const form = new FormData();
        form.append('files', file);
        const res = await api.post(API_ROUTES.uploadProviderImage, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const uploaded = res.data?.uploaded as Array<{ url: string }> | undefined;
        if (uploaded && uploaded.length > 0) {
          updateForm('logo_url', uploaded[0].url);
        }
      } catch {
        // silent
      } finally {
        setIsUploadingLogo(false);
      }
    },
    [updateForm],
  );

  const handleLogoDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const file = Array.from(e.dataTransfer.files).find((f) =>
        ACCEPTED_IMAGE_TYPES.includes(f.type),
      );
      if (file) handleLogoUpload(file);
    },
    [handleLogoUpload],
  );

  return (
    <div className={styles.container}>
      <button className={styles.backButton} onClick={onBack}>
        <FontAwesomeIcon icon={faArrowLeft} className={styles.backArrow} /> Back
      </button>

      <h2 className={styles.title}>Event Details</h2>
      <p className={styles.subtitle}>
        Tell us about your event so the AI can craft the perfect invitation.
      </p>

      <div className={styles.form}>
        <div className={styles.row}>
          <Input
            name="event_name"
            label="Event Name *"
            value={formData.event_name}
            setValue={(v) => updateForm('event_name', v)}
            placeholder="e.g. Sofia's Quinceañera"
          />

          <Select
            label="Event Type"
            value={formData.event_type}
            onChange={(e) => updateForm('event_type', e.target.value)}
            options={EVENT_TYPE_OPTIONS}
            placeholder="Search or select an event type..."
            allowInput
            inputValue={eventTypeSearch}
            onInputChange={setEventTypeSearch}
          />
        </div>

        <div className={styles.row}>
          <Input
            name="event_date"
            label="Date *"
            type="date"
            value={formData.event_date}
            setValue={(v) => updateForm('event_date', v)}
            placeholder="Select a date"
          />
          <Input
            name="venue"
            label="Location / Venue *"
            value={formData.venue}
            setValue={(v) => updateForm('venue', v)}
            placeholder="e.g. Grand Ballroom, Hotel Marriott"
          />
        </div>

        {/* Optional fields toggle section */}
        <div className={styles.optionalSection}>
          <span className={styles.optionalLabel}>Optional details</span>
          <div className={styles.optionalToggles}>
            {OPTIONAL_FIELDS.map(({ key, label }) => {
              const active = visibleOptionals.has(key);
              return (
                <button
                  key={key}
                  type="button"
                  className={`${styles.toggleChip} ${active ? styles.toggleChipActive : ''}`}
                  onClick={() => toggleOptional(key)}
                  aria-pressed={active}
                >
                  <span className={styles.toggleIcon}>{active ? '✓' : ''}</span>
                  {label}
                </button>
              );
            })}
          </div>

          {visibleOptionals.size > 0 && (
            <div className={styles.optionalFields}>
              {visibleOptionals.has('time') && (
                <Input
                  name="time"
                  label="Time"
                  type="time"
                  value={formData.time}
                  setValue={(v) => updateForm('time', v)}
                  placeholder="Select a time"
                />
              )}

              {visibleOptionals.has('dress_code') && (
                <Input
                  name="dress_code"
                  label="Dress Code"
                  value={formData.dress_code}
                  setValue={(v) => updateForm('dress_code', v)}
                  placeholder="e.g. Black Tie, Cocktail, Festive"
                />
              )}

              {visibleOptionals.has('logo') && (
                <div className={styles.logoSection}>
                  <label className={styles.logoLabel}>Event / Company Logo</label>
                  <p className={styles.logoHint}>
                    Upload your logo to include it on the invitation design.
                  </p>

                  {formData.logo_url ? (
                    <div className={styles.logoPreview}>
                      <img src={formData.logo_url} alt="Logo" className={styles.logoImg} />
                      <button
                        type="button"
                        className={styles.logoRemove}
                        onClick={() => updateForm('logo_url', '')}
                        aria-label="Remove logo"
                      >
                        &times;
                      </button>
                    </div>
                  ) : (
                    <div
                      className={`${styles.logoDropzone} ${isUploadingLogo ? styles.logoUploading : ''}`}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={handleLogoDrop}
                      onClick={() => !isUploadingLogo && logoInputRef.current?.click()}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === ' ' || e.key === 'Enter') logoInputRef.current?.click();
                      }}
                      aria-label="Upload logo"
                    >
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept={ACCEPTED_IMAGE_TYPES.join(',')}
                        className={styles.hiddenInput}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleLogoUpload(file);
                          if (e.target) e.target.value = '';
                        }}
                        disabled={isUploadingLogo}
                      />
                      {isUploadingLogo ? (
                        <span className={styles.logoDropzoneText}>Uploading...</span>
                      ) : (
                        <>
                          <span className={styles.logoDropzoneIcon}>&#127912;</span>
                          <span className={styles.logoDropzoneText}>
                            Drag your logo here or <strong>click to select</strong>
                          </span>
                          <span className={styles.logoDropzoneHint}>PNG, JPG, WebP, or SVG</span>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              {visibleOptionals.has('notes') && (
                <>
                  <Input
                    name="special_notes"
                    label="Special Notes / Message"
                    value={formData.special_notes}
                    setValue={setNotesSafe}
                    placeholder="Any special requests, RSVP info, or message for guests..."
                    multiline
                    rows={5}
                  />
                  {isLongNotes && (
                    <p className={styles.notesWarning}>
                      {notesLength.toLocaleString()} characters — long notes are fine, but
                      generation may take longer and cost more tokens. We&apos;ll summarize
                      them before generating the invitation.
                    </p>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <div className={styles.actions}>
        {!canProceed && (
          <p className={styles.hint}>Fill in Event Name, Date, and Location to continue</p>
        )}
        <Button
          variant="primary"
          size="lg"
          onClick={onNext}
          disabled={!canProceed || isUploadingLogo}
        >
          Generate Invitation &rarr;
        </Button>
      </div>
    </div>
  );
};
